import { Suspense, lazy, type ComponentType } from 'react';
import { NotFound404, PageFallback } from '@bookora/ui';

import { Permission } from '@bookora/shared';
import {
  Navigate,
  createBrowserRouter,
  useParams,
  type RouteObject,
  type RouterProviderProps,
} from 'react-router-dom';

import { AuthGate } from '@/auth/auth-gate';
import { RouteErrorBoundary } from '@/components/route-error-boundary';
import { RequirePlanFeature } from '@/features/plan/require-plan-feature';
import { SetupResumeGate } from '@/features/setup/setup-resume-gate';

import { routes as aiImportRoutes } from '@/features/ai-import/routes';
import { routes as calendarRoutes } from '@/features/calendar/routes';
import { routes as inventoryRoutes } from '@/features/inventory/routes';
import { routes as patientRoutes } from '@/features/patients/routes';
import { routes as photoRoutes } from '@/features/photos/routes';
import { routes as sessionRoutes } from '@/features/sessions/routes';
import { routes as settingsRoutes } from '@/features/settings/routes';
import { routes as staffRoutes } from '@/features/staff/routes';

import { RequirePermission } from './require-permission';
import { Root } from './root';
import { SectionPlaceholder } from './section-placeholder';
import { SECTIONS, sectionPermissions, type SectionDef } from './sections';

const LoginPage = lazy(() => import('@/auth/login-page'));
const SignupPage = lazy(() => import('@/auth/signup-page'));
const SetupPage = lazy(() => import('@/features/setup/setup-page'));

/*
 * The shell is its own chunk: the sign-in, signup and setup pages do not
 * download the sidebar, the header, the notification centre and the guidance
 * runtime they never show. Anywhere else the chunk is requested as this
 * module evaluates, in parallel with the boot's `/auth/me`, so a signed-in
 * load does not wait on one more round trip after the splash.
 */
const loadAppLayout = () => import('@/layout/app-layout').then((m) => ({ default: m.AppLayout }));
const AppLayout = lazy(loadAppLayout);
if (
  typeof window !== 'undefined' &&
  !/^\/(login|signup|setup)(\/|$)/.test(window.location.pathname)
) {
  void loadAppLayout().catch(() => undefined);
}

/**
 * The route table, built from `sections.ts`.
 *
 * ── HOW A FEATURE AGENT ADDS SCREENS ────────────────────────────────────────
 *
 *  · **A section's main screen** — edit `sections.ts` only. Set `status:
 *    'ready'` and point `load` at the page module. The route, the sidebar entry,
 *    the breadcrumb and the permission gate all follow.
 *
 *  · **Child routes** (`/patients/new`, `/patients/:id`, `/patients/:id/photos`)
 *    — export a `routes: RouteObject[]` from `src/features/<key>/routes.tsx`
 *    and add it to `EXTRA_ROUTES` below. Wrap them in `<RequirePermission>`
 *    yourself; the section wrapper does not extend to sibling paths.
 *
 * Every screen is lazily loaded, so the initial bundle is the shell plus the
 * dashboard and nothing else — which on an Iranian connection is the difference
 * between a panel that opens and one that is left loading.
 */

/**
 * Child routes contributed by feature folders.
 *
 * Each feature wraps its own routes in `<RequirePermission>` — the section
 * wrapper above does not extend to sibling paths, so a detail route that
 * forgot its guard would be reachable by anyone who can reach the shell.
 */
const EXTRA_ROUTES: RouteObject[] = [
  /*
   * The `link` the server stores on a NEW_REVIEW notification
   * (`directory-reviews.service.ts`, `review-moderation.service.ts`). There
   * was no such screen, so «دیدگاه جدید … می‌تونید پاسخ عمومی ثبت کنید»
   * opened «صفحه پیدا نشد». It lands on the reviews screen with that review's
   * reply drawer open.
   */
  {
    path: 'directory/reviews/:id',
    element: (
      <RequirePermission anyOf={[Permission.DIRECTORY_MANAGE]}>
        <ReviewRedirect />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  ...patientRoutes,
  ...calendarRoutes,
  ...sessionRoutes,
  ...photoRoutes,
  ...staffRoutes,
  ...settingsRoutes,
  ...inventoryRoutes,
  // «ثبت اطلاعات از روی عکس» — no sidebar section; reached from the screens it fills.
  ...aiImportRoutes,
];

function ReviewRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/reviews?review=${encodeURIComponent(id)}` : '/reviews'} replace />;
}

function elementFor(section: SectionDef): React.ReactElement {
  const required = sectionPermissions(section);

  const screen =
    section.status === 'ready' && section.load ? (
      // `lazy` is called during render of the route table, which happens once
      // at module scope — not on every navigation.
      renderLazy(section.load)
    ) : (
      <SectionPlaceholder section={section} />
    );

  // Permission FIRST, then the plan: someone who may not open the section at
  // all is told that (403), not sold an upgrade for a screen their role would
  // still refuse. The plan gate fails open while plan access loads.
  const inner = section.planFeature ? (
    <RequirePlanFeature
      feature={section.planFeature}
      variant="section"
      sectionLabel={section.label}
    >
      {screen}
    </RequirePlanFeature>
  ) : (
    screen
  );

  return required.length > 0 ? (
    <RequirePermission anyOf={required}>{inner}</RequirePermission>
  ) : (
    inner
  );
}

const lazyCache = new Map<() => Promise<{ default: ComponentType }>, ComponentType>();

function renderLazy(load: () => Promise<{ default: ComponentType }>): React.ReactElement {
  let Component = lazyCache.get(load);
  if (!Component) {
    Component = lazy(load);
    lazyCache.set(load, Component);
  }
  return <Component />;
}

/**
 * `errorElement` sits on each LEAF route, not on the shell.
 *
 * React Router bubbles a thrown error to the nearest ancestor that declares
 * one, and renders that element IN PLACE OF the route it is attached to. Put it
 * on the `AppLayout` route and a broken patient list blanks the sidebar, the
 * clinic switcher and the notification bell along with it. On the leaf, only
 * the content area is replaced and the rest of the panel keeps working.
 */
const sectionRoutes: RouteObject[] = SECTIONS.map((section) =>
  section.path === '/'
    ? { index: true, element: elementFor(section), errorElement: <RouteErrorBoundary /> }
    : {
        path: section.path.replace(/^\//, ''),
        element: elementFor(section),
        errorElement: <RouteErrorBoundary />,
      },
);

/**
 * Typed as `RouterProviderProps['router']` rather than left inferred: the
 * inferred type reaches into `@remix-run/router`, which is a transitive
 * dependency and not resolvable from a declaration emit under pnpm's strict
 * node_modules layout.
 */
export const router: RouterProviderProps['router'] = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<PageFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        // Sibling of `/login`, NOT under `<AuthGate>`: the signup wizard runs
        // its first step with no token and its last step with a token but no
        // membership — the gate would bounce both. It navigates into the shell
        // itself once a clinic exists. See `auth/signup-page.tsx`.
        path: '/signup',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SignupPage />
          </Suspense>
        ),
      },
      {
        element: <AuthGate />,
        children: [
          {
            // «راه‌اندازی کلینیک» — full screen, outside the shell on purpose
            // (`features/setup/setup-page.tsx`). Signup lands here.
            path: '/setup',
            element: (
              <RequirePermission anyOf={[Permission.SETTINGS_EDIT]}>
                <Suspense fallback={<PageFallback />}>
                  <SetupPage />
                </Suspense>
              </RequirePermission>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            // Sends an owner whose clinic is not set up from `/` back to the
            // wizard, and completes setup once every required step is done.
            element: <SetupResumeGate />,
            children: [
              {
                element: (
                  <Suspense fallback={<PageFallback />}>
                    <AppLayout />
                  </Suspense>
                ),
                children: [
                  ...sectionRoutes,
                  ...EXTRA_ROUTES,
                  { path: '*', element: <NotFound404 /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
